'use client';

import { c as _c } from "react/compiler-runtime";
import { CheckIcon, CopyIcon } from 'lucide-react';
import mergeRefs from 'merge-refs';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
import { getIconByLanguage } from '@/utils/get-icon-by-language';
import { Button } from './button';
import { ScrollArea } from './scroll-area';
type CodeBlockProps = {
  'data-lang'?: string;
  figureClassName?: string;
  scrollAreaClassName?: string;
} & React.ComponentProps<'pre'>;
export function CodeBlock(props) {
  const $ = _c(33);
  let children;
  let className;
  let figureClassName;
  let lang;
  let ref;
  let rest;
  let scrollAreaClassName;
  let title;
  if ($[0] !== props) {
    ({
      children,
      className,
      title,
      "data-lang": lang,
      figureClassName,
      scrollAreaClassName,
      ref,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = figureClassName;
    $[4] = lang;
    $[5] = ref;
    $[6] = rest;
    $[7] = scrollAreaClassName;
    $[8] = title;
  } else {
    children = $[1];
    className = $[2];
    figureClassName = $[3];
    lang = $[4];
    ref = $[5];
    rest = $[6];
    scrollAreaClassName = $[7];
    title = $[8];
  }
  const preRef = useRef(null);
  const t0 = lang ?? "";
  let t1;
  if ($[9] !== t0) {
    t1 = getIconByLanguage(t0);
    $[9] = t0;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const Icon = t1;
  let t2;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function onCopy() {
      navigator.clipboard.writeText(preRef.current?.textContent ?? "");
    };
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const onCopy = t2;
  let t3;
  if ($[12] !== figureClassName) {
    t3 = cn("not-prose group relative my-6 overflow-hidden rounded-2xl border bg-input/30 text-sm", figureClassName);
    $[12] = figureClassName;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== Icon || $[15] !== title) {
    t4 = title ? <div className="flex flex-row items-center gap-2 border-b bg-muted px-4 py-1.5"><div className="text-muted-foreground"><Icon className="size-3.5" /></div><figcaption className="flex-1 truncate text-muted-foreground">{title}</figcaption><CopyButton className="-me-2" onCopy={onCopy} /></div> : <CopyButton className="absolute top-2 right-2 z-10" onCopy={onCopy} />;
    $[14] = Icon;
    $[15] = title;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== ref) {
    t5 = mergeRefs(preRef, ref);
    $[17] = ref;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== className) {
    t6 = cn("p-4 text-[13px]", className);
    $[19] = className;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== children || $[22] !== rest || $[23] !== t5 || $[24] !== t6) {
    t7 = <pre ref={t5} className={t6} {...rest}>{children}</pre>;
    $[21] = children;
    $[22] = rest;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== scrollAreaClassName || $[27] !== t7) {
    t8 = <ScrollArea className={scrollAreaClassName}>{t7}</ScrollArea>;
    $[26] = scrollAreaClassName;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t3 || $[30] !== t4 || $[31] !== t8) {
    t9 = <figure className={t3}>{t4}{t8}</figure>;
    $[29] = t3;
    $[30] = t4;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
}
type CopyButtonProps = {
  onCopy: () => void;
} & React.ComponentProps<typeof Button>;
function CopyButton(props) {
  const $ = _c(21);
  let className;
  let onCopy;
  let rest;
  if ($[0] !== props) {
    ({
      onCopy,
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = onCopy;
    $[3] = rest;
  } else {
    className = $[1];
    onCopy = $[2];
    rest = $[3];
  }
  const [isCopied, setIsCopied] = useState(false);
  const t = useTranslations();
  let t0;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const copyResetTimeoutId = setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      return () => {
        clearTimeout(copyResetTimeoutId);
      };
    };
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== isCopied) {
    t1 = [isCopied];
    $[5] = isCopied;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  useEffect(t0, t1);
  let t2;
  if ($[7] !== onCopy) {
    t2 = () => {
      onCopy();
      setIsCopied(true);
    };
    $[7] = onCopy;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== className) {
    t3 = cn("size-7.5 opacity-0 transition-opacity group-hover:opacity-100", className);
    $[9] = className;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t) {
    t4 = t("common.aria-labels.copy-code");
    $[11] = t;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== isCopied) {
    t5 = isCopied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />;
    $[13] = isCopied;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== rest || $[16] !== t2 || $[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <Button variant="ghost" size="icon" onClick={t2} className={t3} aria-label={t4} {...rest}>{t5}</Button>;
    $[15] = rest;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}